/**
 * The two-tone monospace chip an entity's stable backend id wears
 * (design-system.md §6 "ID chip"; `.id-chip` in the prototype's
 * contest-overview.css).
 *
 * A grey label box butted against a white value box, sharing one hairline
 * border — hence `alignItems: stretch` and the overflow clip, which is what lets
 * the two halves meet the rounded corner cleanly.
 */
import Box from '@mui/material/Box';
import type { Theme } from '@mui/material/styles';

interface IdChipProps {
  /** The short word in the grey box. `ID` unless the id belongs to something else. */
  label?: string;
  value: string;
}

export const IdChip = ({ label = 'ID', value }: IdChipProps) => (
  <Box
    component="span"
    sx={(theme: Theme) => ({
      display: 'inline-flex',
      alignItems: 'stretch',
      flexShrink: 0,
      height: '24px',
      borderRadius: `${theme.tokens.radius.idChip}px`,
      border: `${theme.tokens.border.width} solid ${theme.tokens.color.lineStrong}`,
      backgroundColor: theme.tokens.color.surface,
      overflow: 'hidden',
      fontFamily: theme.tokens.font.mono,
      whiteSpace: 'nowrap',
    })}
  >
    <Box
      component="span"
      sx={(theme: Theme) => ({
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0 7px',
        backgroundColor: theme.tokens.componentColor.idChipKeyBg,
        color: theme.tokens.color.ink4,
        borderRight: `${theme.tokens.border.width} solid ${theme.tokens.color.lineStrong}`,
        fontSize: '10.5px',
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
      })}
    >
      {label}
    </Box>
    <Box
      component="span"
      sx={(theme: Theme) => ({
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0 9px',
        color: theme.tokens.color.ink2,
        fontFamily: theme.tokens.font.mono,
        fontSize: '11.5px',
        fontWeight: 500,
        letterSpacing: '-0.005em',
        fontVariantNumeric: 'tabular-nums',
      })}
    >
      {value}
    </Box>
  </Box>
);
